import { useActorRef, useSelector } from '@xstate/react';
import { type SnapshotFrom } from 'xstate';

import { useClaimOfferActor } from './actors';
import { clickToClaimMachine, type ClickToClaimMachineImplementations } from './click-to-claim.machine'; // prettier-ignore
import { type ClaimStatus, type Input } from './click-to-claim.machine.types';

// ─────────────────────────────────────────────────────────────────────────────

export const useClickToClaimMachine = (
  input: Input,
  implementations?: ClickToClaimMachineImplementations,
) => {
  const { claimOffer } = useClaimOfferActor();

  // ─── Machine ─────────────────────────────────────────────────────────

  const actorRef = useActorRef(
    clickToClaimMachine.provide({
      actors: {
        claimOffer,
        ...implementations?.actors,
      },
      actions: {
        ...implementations?.actions,
      },
      guards: {
        ...implementations?.guards,
      },
    }),
    { input },
  );

  // ─── Status ──────────────────────────────────────────────────────────

  const status = useSelector(actorRef, getClaimStatus);
  const reward = useSelector(actorRef, (snapshot) => snapshot.context.reward);

  // ─────────────────────────────────────────────────────────────────────

  return { status, reward };
};

// ─── Pure Functions ──────────────────────────────────────────────────────────

const getClaimStatus = (snapshot: ClickToClaimSnapshot): ClaimStatus => {
  switch (true) {
    case snapshot.matches('claiming'): {
      return 'claiming';
    }
    case snapshot.matches('claimed'): {
      return 'claimed';
    }
    case snapshot.matches({ error: 'already-claimed' }): {
      return 'alreadyClaimed';
    }
    case snapshot.matches({ error: 'new-user-only' }): {
      return 'newUserOnly';
    }
    case snapshot.matches({ error: 'not-eligible' }): {
      return 'notEligible';
    }
    case snapshot.matches({ error: 'expired' }): {
      return 'expired';
    }
    default: {
      return 'unknownError';
    }
  }
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ClickToClaimSnapshot = SnapshotFrom<typeof clickToClaimMachine>;
